import { format } from "date-fns";
import { Link } from "react-router-dom";
import { useDashboardRecent } from "../../hooks/use-api";
import { Badge } from "../ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";

const RECENT_RECORDS_LIMIT = 30;

const statusVariant: Record<string, "default" | "secondary" | "destructive"> = {
  Active: "default",
  Cancelled: "destructive",
  Superseded: "secondary",
};

export function RecentRecords(): React.ReactElement {
  const { data } = useDashboardRecent(RECENT_RECORDS_LIMIT);
  const records = data?.records ?? [];

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold">Recent Records</CardTitle>
          <Link to="/insurance" className="text-xs text-primary hover:underline" data-testid="recent-records-view-all">
            View all records
          </Link>
        </div>
      </CardHeader>
      <CardContent>
        {records.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">No recent records yet.</p>
        ) : (
          <Table data-testid="recent-records-table">
            <TableHeader>
              <TableRow>
                <TableHead>Address</TableHead>
                <TableHead>Insured</TableHead>
                <TableHead>Valuer</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Updated</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {records.map((record) => (
                <TableRow
                  key={record.id}
                  className={record.status === "Cancelled" ? "opacity-60" : ""}
                  data-testid={`recent-record-row-${record.id}`}
                >
                  <TableCell>
                    <Link
                      to={`/insurance/${record.id}`}
                      className="text-primary hover:underline font-medium"
                      data-testid={`recent-record-link-${record.id}`}
                    >
                      {record.address || `Record #${record.id}`}
                    </Link>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{record.insured || "—"}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {record.valuer === "—" ? <span className="text-orange-500 text-xs">No valuer</span> : record.valuer}
                  </TableCell>
                  <TableCell>
                    <Badge variant={statusVariant[record.status] ?? "default"}>{record.status}</Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground text-xs whitespace-nowrap">
                    {format(new Date(record.updatedAt), "dd MMM yyyy")}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
